import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useAnimation, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';

const EnhancedInteractiveTimeline = () => {
  // Change from activeYear to activeMilestoneId
  const [activeMilestoneId, setActiveMilestoneId] = useState(1);
  const [isHovered, setIsHovered] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const containerRef = useRef(null);
  const timelineRef = useRef(null);

  // Animation controls
  const controls = useAnimation();
  const ref = useRef(null);
  const inView = useInView(ref, {
    threshold: 0.3,
    triggerOnce: true
  });

  // Timeline milestones
  const milestones = [
    {
      id: 1,
      year: 2005,
      title: "Foundation",
      description: "In 2005, operations began in iron trading and real estate, focusing on supplying construction materials and developing residential and commercial properties—laying the groundwork for future business expansion.",
      stat: { title: "Established", value: "2005" },
      image: "https://ik.imagekit.io/tdlebsr5e/WhatsApp%20Image%202025-04-28%20at%2018.49.17%20(1).jpeg?updatedAt=1747492074344"
    },
    {
      id: 2,
      year: 2011,
      title: "Expansion and Diversification",
      description: "In 2011, the group expanded into the automobile sector by launching a Škoda dealership, marking its entry into the automotive retail industry.",
      stat: { title: "Expansion", value: "Regional" },
      image: "https://ik.imagekit.io/jncw2kb8u/kristian-valco-jUzSmjHILnc-unsplash.jpg?updatedAt=1746036704848"
    },
    {
      id: 3,
      year: 2014,
      title: "Automotive Sector Expansion",
      description: "In 2014, a Hyundai dealership was established, expanding the business's presence in the automotive sector.",
      stat: { title: "Vertical", value: "Automobile" },
      image: "https://ik.imagekit.io/jncw2kb8u/the-punisher-PuhUJ3Cgdew-unsplash.jpg?updatedAt=1746036868821"
    },
    {
      id: 4,
      year: 2018,
      title: "Hyundai Expansion – Shimla",
      description:
        "In 2018, the group expanded its Hyundai operations with the launch of a new dealership in Shimla, extending its reach into the Himachal hill region.",
      stat: { title: "Location", value: "Shimla" },
      image: "https://ik.imagekit.io/tdlebsr5e/devbhoomishimla.webp?updatedAt=1747479569482", // Replace with your actual Shimla image URL
    },
    {
      id: 5,
      year: 2019,
      title: "Launch of The Gulmohar Grand",
      description: "In 2019, the business entered the hospitality industry with the launch of The Gulmohar Grand, a premier hotel aimed at providing high-quality accommodation and services to both business and leisure travelers.",
      stat: { title: "Companies", value: "Growing" },
      image: "https://ik.imagekit.io/jncw2kb8u/hotel-in-una1.jpg?updatedAt=1745481996268"
    },
    {
      id: 6,
      year: 2019,
      title: "Launch of Grand Cinemaz",
      description:
        "In 2019, the group ventured into the entertainment industry by launching Grand Cinemaz — a modern cinema experience featuring 2K projection, premium seating, and curated hospitality for an elevated moviegoing experience.",
      stat: { title: "New Vertical", value: "Entertainment" },
      image: "https://ik.imagekit.io/tdlebsr5e/grand-cinemaz.jpg?updatedAt=1747479361078",
    },
    {
      id: 7,
      year: 2020,
      title: "Standing Together During the Pandemic",
      description:
        "In 2020, amid the global COVID-19 crisis, we stood firmly with our team. Every employee received full salary without a single deduction, even while safely at home—upholding our values of trust, care, and unity.",
      stat: { title: "Core Values", value: "Trust and Care" },
      image: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/PICTURES/EVENTS/ROOFTOP%20RECEPTION/covid-virus-3d-modeling.jpg?updatedAt=1747401460971" // (You can update the image to a relevant one)
    },
    {
      id: 8,
      year: 2021,
      title: "Further growth",
      description: "In 2021, the business further expanded its automotive presence with the addition of another car dealership, continuing to strengthen its foothold in the automobile sector.",
      stat: { title: "Verticals", value: "Four Industries" },
      image: "https://ik.imagekit.io/jncw2kb8u/man-giving-bar-graph-presentation-using-high-technology-digital-pen.jpg?updatedAt=1746009494715"
    },
    {
      id: 9,
      year: 2025,
      title: "Growth and Green Energy",
      description:
        "In 2025, the group operated seven companies across four distinct verticals, employing hundreds of professionals and contributing significantly to the regional economy of Himachal Pradesh. The group also advanced sustainable development through solar power solutions under its FDL Solar Power division, driving clean energy adoption across the region.",
      stat: { title: "Verticals", value: "Four Industries + Green Energy" },
      image: "https://ik.imagekit.io/jncw2kb8u/jakub-zerdzicki-U4-I4oH4xlg-unsplash.jpg?updatedAt=1746009925190"
    },
    {
      id: 10,
      year: 2026,
      title: "Present Day",
      description:
        "In 2026, the group expanded its automotive presence with the addition of a Tata Motors dealership. Building on its Škoda and Hyundai operations, this milestone strengthens the group's automobile portfolio and brings more choice to customers in Himachal Pradesh.",
      stat: { title: "Automotive Expansion", value: "Tata Motors" },
      image: "https://img.freepik.com/free-photo/where-i-need-sign-female-customer-modern-stylish-bearded-businessman-automobile-saloon_146671-16730.jpg"
    }

  ];

  // Find active milestone by id instead of year
  const activeMilestone = milestones.find(milestone => milestone.id === activeMilestoneId) || milestones[0];

  // Company data for sidebar
  const companyData = [
    { label: "Founded", value: "2005" },
    { label: "Headquarters", value: "Himachal Pradesh" },
    { label: "Companies", value: "Seven and Growing" },
    { label: "Employees", value: "500+" },
    { label: "Verticals", value: "Four Industries" }
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }
    }
  };

  const timelineVariants = {
    hidden: { opacity: 0, scaleX: 0 },
    visible: {
      opacity: 1,
      scaleX: 1,
      transition: { duration: 1, ease: [0.25, 0.1, 0.25, 1] }
    }
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }
    },
    exit: {
      opacity: 0,
      y: -50,
      transition: { duration: 0.5 }
    }
  };

  const circleVariants = {
    inactive: {
      scale: 1,
      backgroundColor: "#ffffff",
      borderColor: "#d1d5db",
      transition: { duration: 0.3 }
    },
    active: {
      scale: 1.4,
      backgroundColor: "#f59e0b",
      borderColor: "#f59e0b",
      transition: { duration: 0.3 }
    },
    hover: {
      scale: 1.5,
      backgroundColor: "#f59e0b",
      borderColor: "#f59e0b",
      transition: { duration: 0.2 }
    }
  };

  // Auto-scroll functionality - changed to use id
  const goToNextSlide = useCallback(() => {
    const currentIndex = milestones.findIndex(m => m.id === activeMilestoneId);
    const nextIndex = (currentIndex + 1) % milestones.length;
    setActiveMilestoneId(milestones[nextIndex].id);
  }, [activeMilestoneId, milestones]);

  // Auto-scroll effect
  useEffect(() => {
    let intervalId;

    if (inView && !isPaused) {
      intervalId = setInterval(() => {
        goToNextSlide();
      }, 5000); // Change slide every 5 seconds
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [inView, isPaused, goToNextSlide]);

  // Start animations when in view
  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  // Pause auto-scroll when user is interacting
  const handleTimelineInteraction = () => {
    setIsPaused(true);

    // Resume after 10 seconds of inactivity
    const timeout = setTimeout(() => {
      setIsPaused(false);
    }, 10000);

    return () => clearTimeout(timeout);
  };

  return (
    <div ref={containerRef} className="w-full bg-gradient-to-b from-white to-amber-50 dark:from-gray-950 dark:to-gray-900 dark:bg-mesh-pattern py-16 md:py-28 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header section with enhanced design */}
        <motion.div
          ref={ref}
          initial="hidden"
          animate={controls}
          variants={containerVariants}
          className="text-center mb-16"
        >
          <motion.div variants={itemVariants} className="inline-block mb-3">
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 dark:from-amber-600 dark:to-amber-700 text-white px-6 py-2 rounded-full text-sm font-bold shadow-md">
              Our Journey Since 2005
            </div>
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="relative mb-6 flex justify-center flex-wrap gap-2"
          >
            {/* Each word animated attractively */}
            {[
              { word: "DEV", y: -8, rotate: -3, delay: 0 },
              { word: "BHOOMI", y: -12, rotate: -2, delay: 0.2 },
              { word: "GROUP", y: -16, rotate: -1, delay: 0.4 },
              { word: "COMPANIES", y: -20, rotate: -1, delay: 0.6 },
            ].map(({ word, y, rotate, delay }, idx) => (
              <motion.span
                key={idx}
                animate={{
                  y: [0, y, 0],
                  rotate: [0, rotate, 0],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  repeatType: "mirror",
                  ease: "easeInOut",
                  delay,
                }}
                className="text-5xl md:text-6xl font-extrabold leading-tight tracking-widest 
          text-transparent bg-clip-text bg-gradient-to-r from-[#BA772B] via-[#BA772B] to-[#BA772B]
          dark:from-yellow-300 dark:via-amber-400 dark:to-yellow-500
          drop-shadow-[0_5px_15px_rgba(255,191,0,0.5)]"
              >
                {word}
              </motion.span>
            ))}

            {/* Premium glowing aura */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.4, 0.6, 0.4],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  repeatType: "mirror",
                  ease: "easeInOut",
                }}
                className="w-64 h-24 bg-amber-400/30 blur-3xl rounded-full"
              ></motion.div>
            </div>
          </motion.div>

          <motion.p
            variants={itemVariants}
            className="max-w-3xl mx-auto text-lg text-gray-600 dark:text-gray-300"
          >
            Founded by Mr. Rajinder Vashista, Dev Bhoomi Group Companies has grown from a single venture
            to a diversified group with seven companies across four verticals, becoming a regional powerhouse
            in Himachal Pradesh.
          </motion.p>
        </motion.div>



        {/* Main content grid with enhanced design */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 mb-16">
          {/* Left sidebar with enhanced design */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="lg:col-span-3 bg-white dark:bg-gray-800 dark:bg-opacity-80 dark:backdrop-blur-sm p-6 rounded-xl h-fit sticky top-8 shadow-xl border-l-4 border-amber-500 dark:border-amber-400"
          >
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center">
              <span className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 dark:from-amber-600 dark:to-amber-700 rounded-full mr-3 flex items-center justify-center text-white text-sm font-bold shadow-md">
                DB
              </span>
              Company Facts
            </h3>

            <div className="space-y-4">
              {companyData.map((item, index) => (
                <div
                  key={index}
                  className="border-b border-gray-200 dark:border-gray-700 pb-4 last:border-0 hover:bg-amber-50 dark:hover:bg-gray-700 transition-colors p-2 rounded"
                >
                  <p className="text-sm text-amber-600 dark:text-amber-500 font-medium">{item.label}</p>
                  <p className="text-gray-900 dark:text-gray-200 font-bold mt-1">{item.value}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Main timeline content with enhanced design */}
          <div className="lg:col-span-9">
            {/* Enhanced timeline */}
            <div className="mb-12 relative" onClick={handleTimelineInteraction}>
              <motion.div
                ref={timelineRef}
                initial="hidden"
                animate="visible"
                variants={timelineVariants}
                className="h-2 bg-gray-200 dark:bg-gray-700 dark:bg-opacity-60 absolute top-6 left-0 right-0 origin-left rounded-full shadow-inner dark:shadow-gray-950"
              ></motion.div>

              <div className="flex justify-between relative">
                {milestones.map((milestone) => (
                  <div key={milestone.id} className="flex flex-col items-center relative">
                    <motion.button
                      initial="inactive"
                      animate={activeMilestoneId === milestone.id ? "active" : "inactive"}
                      whileHover="hover"
                      variants={circleVariants}
                      onClick={() => {
                        setActiveMilestoneId(milestone.id);
                        handleTimelineInteraction();
                      }}
                      className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 dark:hover:bg-gray-700 relative z-10 focus:outline-none shadow-md dark:shadow-inner dark:shadow-gray-950"
                      aria-label={`View milestone for year ${milestone.year}`}
                    ></motion.button>
                    <motion.div className="relative">
                      <motion.p
                        animate={{
                          scale: activeMilestoneId === milestone.id ? 1.1 : 1,
                          y: activeMilestoneId === milestone.id ? -4 : 0,
                          fontWeight: activeMilestoneId === milestone.id ? 700 : 500
                        }}
                        className={`text-sm font-medium mt-3 transition-colors ${activeMilestoneId === milestone.id ? 'text-amber-600 dark:text-amber-400 dark:drop-shadow-sm dark:drop-shadow-amber-500/20' : 'text-gray-500 dark:text-gray-400'
                          }`}
                      >
                        {milestone.year}
                      </motion.p>
                    </motion.div>
                  </div>
                ))}
              </div>

              {/* Enhanced progress line - updated to use IDs */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{
                  scaleX: milestones.findIndex(m => m.id === activeMilestoneId) / (milestones.length - 1) || 0
                }}
                transition={{ duration: 0.5 }}
                className="h-2 bg-gradient-to-r from-amber-400 to-amber-600 dark:from-amber-500 dark:via-amber-600 dark:to-amber-700 absolute top-6 left-0 origin-left rounded-full shadow-sm dark:shadow-amber-900/30 dark:glow-amber-sm"
                style={{
                  width: '100%',
                  transformOrigin: 'left'
                }}
              ></motion.div>
            </div>

            {/* Enhanced content for current milestone - now keyed by id */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeMilestoneId}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={contentVariants}
                className="bg-white dark:bg-gray-800 dark:bg-gradient-to-br dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-700 dark:border-opacity-50"
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
              >
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <div
                    className="relative h-72 md:h-auto overflow-hidden"
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                  >
                    <motion.div
                      animate={{ scale: isHovered ? 1.05 : 1 }}
                      transition={{ duration: 0.4 }}
                      className="absolute inset-0"
                    >
                      <img
                        src={activeMilestone.image}
                        alt={activeMilestone.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-amber-900/70 to-transparent dark:from-gray-950/90 dark:via-gray-900/70 dark:to-gray-900/10"></div>
                    </motion.div>

                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2, duration: 0.5 }}
                      >
                        <p className="text-lg font-medium">{activeMilestone.year}</p>
                        <h3 className="text-3xl font-bold">{activeMilestone.title}</h3>
                      </motion.div>
                    </div>
                  </div>

                  <div className="p-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 dark:from-amber-600 dark:to-amber-800 text-white flex items-center justify-center mr-4 shadow-md dark:shadow-amber-700/20 dark:ring-1 dark:ring-amber-500/30">
                        <span className="text-xl font-bold">{activeMilestone.year.toString().slice(-2)}</span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{activeMilestone.title}</h3>
                        <p className="text-amber-600 dark:text-amber-500 text-sm font-medium">
                          {activeMilestone.stat.title}: {activeMilestone.stat.value}
                        </p>
                      </div>
                    </div>

                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
                      {activeMilestone.description}
                    </p>

                    <div className="flex justify-between items-center">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          const currentIndex = milestones.findIndex(m => m.id === activeMilestoneId);
                          const prevIndex = Math.max(0, currentIndex - 1);
                          setActiveMilestoneId(milestones[prevIndex].id);
                          handleTimelineInteraction();
                        }}
                        disabled={activeMilestoneId === milestones[0].id}
                        className={`p-3 rounded-full ${activeMilestoneId === milestones[0].id
                          ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-amber-100 dark:hover:bg-gray-700 hover:text-amber-600 dark:hover:text-amber-500'
                          } transition-colors shadow-sm`}
                        aria-label="Previous milestone"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                      </motion.button>

                      <div className="text-center px-4 py-1 bg-amber-50 dark:bg-gray-800 dark:bg-opacity-80 dark:backdrop-blur-sm dark:ring-1 dark:ring-amber-600/20 rounded-full">
                        <span className="text-sm text-amber-700 dark:text-amber-400 font-medium">
                          {milestones.findIndex(m => m.id === activeMilestoneId) + 1} of {milestones.length}
                        </span>
                      </div>

                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          const currentIndex = milestones.findIndex(m => m.id === activeMilestoneId);
                          const nextIndex = Math.min(milestones.length - 1, currentIndex + 1);
                          setActiveMilestoneId(milestones[nextIndex].id);
                          handleTimelineInteraction();
                        }}
                        disabled={activeMilestoneId === milestones[milestones.length - 1].id}
                        className={`p-3 rounded-full ${activeMilestoneId === milestones[milestones.length - 1].id
                          ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-amber-100 dark:hover:bg-gray-700 hover:text-amber-600 dark:hover:text-amber-500'
                          } transition-colors shadow-sm`}
                        aria-label="Next milestone"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </motion.button>
                    </div>

                    {/* Auto-scroll indicator */}
                    <div className="flex items-center justify-center mt-6">
                      <button
                        onClick={() => setIsPaused(!isPaused)}
                        className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-500"
                      >
                        {isPaused ? (
                          <>
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            </svg>
                            <span>Resume auto-play</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>Pause auto-play</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Enhanced Bottom CTA */}
        <div className="text-center">
          <Link to={"/about"}>
            <motion.a
              whileHover={{ scale: 1.05, boxShadow: "0 10px 25px -5px rgba(245, 158, 11, 0.4)" }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 dark:from-amber-600 dark:to-amber-800 text-white font-medium rounded-lg shadow-lg hover:from-amber-600 hover:to-amber-700 dark:hover:from-amber-700 dark:hover:to-amber-900 transition-all duration-300 dark:ring-2 dark:ring-amber-500/20 dark:hover:ring-amber-400/30"
            >
              <span>Discover Our Full Story</span>
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </motion.a>
          </Link>
          {/* Auto-sliding indicator */}
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
            Timeline automatically advances every 5 seconds
          </p>
        </div>
      </div>
    </div>
  );
};

export default EnhancedInteractiveTimeline;
